export const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.miniclick.calltrackmanage';
/** CallSync – Call Organizer, the iPhone staff app. Without a country the App Store opens the person's own store. */
export const APP_STORE_URL = 'https://apps.apple.com/app/callsync-call-organizer/id6829809471';

/**
 * Google Play link that hands the pairing code to the app through the Play Install Referrer.
 * The Android app reads `pair=<code>` on first launch (InstallReferrerManager) and fills it in
 * at “Link to your business”, so the person doesn't type the code.
 */
export function playInstallUrl(code?: string | null): string {
  const pairing = code?.trim() || '';
  return pairing ? `${PLAY_STORE_URL}&referrer=${encodeURIComponent(`pair=${pairing}`)}` : PLAY_STORE_URL;
}

/** Opens the installed app with the code (Android MainActivity and the iPhone app both handle callsync://pair/<code>). */
export function appPairUrl(code: string): string {
  return `callsync://pair/${encodeURIComponent(code.trim())}`;
}

export type PhonePlatform = 'android' | 'iphone';

/** Which store fits this browser; null on a computer. iPadOS reports a Mac, so touch points tell them apart. */
export function phonePlatform(userAgent: string, touchPoints = 0): PhonePlatform | null {
  if (/iPhone|iPad|iPod/.test(userAgent) || (/Macintosh/.test(userAgent) && touchPoints > 1)) return 'iphone';
  if (/Android/.test(userAgent)) return 'android';
  return null;
}

/** The store for this browser: the App Store on an iPhone or iPad, otherwise Google Play with the code. */
export function storeInstallUrl(platform: PhonePlatform | null, code?: string | null): string {
  return platform === 'iphone' ? APP_STORE_URL : playInstallUrl(code);
}

/**
 * One install link for both phones, for QR codes and buttons whose reader's phone isn't known:
 * /api/install sends an iPhone to the App Store, an Android phone to Google Play with the code,
 * and a computer to the setup guide.
 */
export function anyPhoneInstallUrl(code?: string | null, origin = 'https://dash.callsyncapp.com'): string {
  const pairing = code?.trim() || '';
  return `${origin}/api/install${pairing ? `?code=${encodeURIComponent(pairing)}` : ''}`;
}

/** Where /api/install sends a browser. */
export function installRedirect(userAgent: string, code: string | null | undefined, helpOrigin: string): string {
  const pairing = code?.trim() || '';
  const platform = phonePlatform(userAgent);
  if (platform === 'iphone') return APP_STORE_URL;
  if (platform === 'android') return playInstallUrl(pairing);
  return `${helpOrigin}/employee-app-setup/${pairing ? `?code=${encodeURIComponent(pairing)}` : ''}`;
}
